import { contactRows, contactsUnreadyMessage } from '@/lib/contactsRead';
import { loanRows, loansUnreadyMessage } from '@/lib/loansDebtsRead';
import { useMemo, useState } from 'react';
import type {
  FinanceContactDto,
  LoanDto,
  LoanGuarantorInput,
  SetLoanAgreementRequest,
} from '@signalvote/shared';
import { ContactKind } from '@signalvote/shared';
import {
  useDonorPickerContacts,
  useFinanceContacts,
  useFinanceLoans,
  useCreateLoan,
  useUpdateLoanBalance,
  useSetLoanGuarantors,
  useSetLoanAgreement,
  useVoidLoan,
  useUnvoidLoan,
} from '@/hooks/queries';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { ContactCombobox } from '@/components/finance/shared/classification';
import { readyProfile } from '@/lib/complianceProfileRead';

// Candidate personal-funds loan manual entry (Schedule C). The treasurer
// records the loan once, then maintains its outstanding balance + paid-to-date as
// repayments are made (a repayment is a separate SB19A expenditure; v1 does NOT auto-link
// the repayment bank-line). The loan drives the federal Form 3 Schedule A SA13A receipt
// (incurred period) + Schedule C SC/10 ledger (while balance > 0). Tailwind + shadcn idiom;
// no MUI.

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export function LoansCard() {
  const loansRead = useFinanceLoans(undefined);
  const loans = loanRows(loansRead);
  const loansUnready = loansUnreadyMessage(loansRead);
  const isLoading = loansRead.status === 'loading';
  // Lending institutions keep the whole book: a bank is an organization contact that need never
  // have given. Only the lender picker narrows to people who have given.
  const contactsRead = useFinanceContacts({ pageSize: 200 });
  const contactsUnready = contactsUnreadyMessage(contactsRead);
  const createLoan = useCreateLoan();

  // #480 — resolve the loan-card framing from the profile's DECLARED loan policy, never an
  // isAlabama/isFederal binary over a five-way domain (federal, AL, VA, NC, LA). The old else
  // branch told every non-AL filer their loan "appears on Schedule C of your federal report" —
  // a federal-only statement that is false for VA/NC/LA. A jurisdiction with a lettered loan
  // schedule (federal Schedule C, VA Schedule E, LA Schedule B) names it; a null-schedule state
  // (AL files a receipt, NC files on its disclosure report) gets neutral report framing that
  // names no wrong agency.
  const profile = readyProfile(useComplianceProfile());

  // The federal Form 3 builder files bank / third-party loans end-to-end: their Schedule C
  // ledger (SC/10 line 13b + SA13B receipt + SB19B repayment split), the loan AGREEMENT
  // (Schedule C-1), and any GUARANTORS (Schedule C-2). So a federal treasurer records a bank
  // loan and fills its agreement + guarantors in-app — the FEC portal is only a fallback if
  // details are missing (surfaced by the re-scoped preflight warning, #73). The Schedule C-1
  // agreement editor stays federal-gated below.
  const isFederal = profile?.level === 'federal';

  const loanScheduleName = profile?.loanScheduleName ?? null;
  // Only the disclosure clause varies by jurisdiction; the lead-in and the "stays open until
  // repaid" tail are shared, so build the sentence from one variable middle to keep the two
  // paths from drifting apart.
  const loanDisclosure = loanScheduleName
    ? `It appears on ${loanScheduleName} of your ${isFederal ? 'federal report' : 'report'}`
    : `It is disclosed on your ${profile?.stateDisplayName ?? 'state'} campaign finance report`;
  const loanIntro = `Record a loan to the campaign (for example, the candidate lending personal funds). ${loanDisclosure} and stays open until you repay the balance to zero. Update the balance here as you repay it.`;

  const [contactId, setContactId] = useState('');
  const lenderPicker = useDonorPickerContacts(contactId);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [interestRate, setInterestRate] = useState('0');
  const [isPersonalFunds, setIsPersonalFunds] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const contactOptions = contactRows(contactsRead);
  // The Schedule C-1 lender block is an organization name (SC1 seq 5) and the server refuses
  // anything else, so an individual is never offered as a lending institution. Computed once
  // here rather than per loan row.
  const institutionOptions = useMemo(
    () => contactOptions.filter((c) => c.contactKind === ContactKind.Organization),
    [contactOptions],
  );

  const canSubmit = contactId !== '' && Number(amount) > 0 && date !== '';

  const handleCreate = async () => {
    setError(null);
    try {
      await createLoan.mutateAsync({
        contactId,
        amount: Number(amount),
        date,
        dueDate: dueDate || null,
        interestRate: interestRate === '' ? null : Number(interestRate),
        notes: null,
        isPersonalFunds,
      });
      setAmount('');
      setDate('');
      setDueDate('');
      setInterestRate('0');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record loan');
    }
  };

  return (
    <div className="rounded-lg border bg-white p-6">
      <h3 className="text-lg font-semibold">Candidate loans</h3>
      <p className="mt-1 text-sm text-gray-600">{loanIntro}</p>

      {/* Existing loans */}
      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-500">Loading loans…</p>}
        {loansUnready && (
          <p role="alert" className="text-sm text-red-600">
            {loansUnready}
          </p>
        )}
        {loansRead.status === 'ready' && loans.length === 0 && (
          <p className="text-sm text-gray-500">No loans recorded yet.</p>
        )}
        {loans.map((loan) => (
          <LoanRow
            key={loan.id}
            loan={loan}
            isFederal={isFederal}
            institutionOptions={institutionOptions}
          />
        ))}
      </div>

      {/* New loan form */}
      <div className="mt-6 border-t pt-4">
        <h4 className="text-sm font-medium">Record a new loan</h4>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block font-medium">Lender</span>
            <ContactCombobox
              ariaLabel="Lender"
              value={contactId}
              onChange={setContactId}
              contacts={lenderPicker.contacts}
              unready={lenderPicker.unready}
              prospects={lenderPicker.prospects}
              onSearchChange={lenderPicker.onSearchChange}
              placeholder="Select a contact…"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Amount</span>
            <input
              aria-label="Amount"
              type="number"
              min={0}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Date</span>
            <input
              aria-label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Due date (optional)</span>
            <input
              aria-label="Due date"
              type="text"
              placeholder='e.g. "On demand" or 12/31/2026'
              maxLength={15}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Interest rate (%)</span>
            <input
              aria-label="Interest rate"
              type="number"
              min={0}
              step={0.01}
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 self-end text-sm">
            <input
              type="checkbox"
              checked={isPersonalFunds}
              onChange={(e) => setIsPersonalFunds(e.target.checked)}
              className="h-4 w-4 rounded border"
            />
            <span>Candidate personal funds</span>
          </label>
        </div>

        {/* A federal bank / third-party loan is now filed fully in-app: its Schedule C
            ledger, its loan agreement (Schedule C-1), and any guarantors (Schedule C-2).
            After you record it below, fill in the loan-agreement and guarantor sections on the
            loan row. The FEC portal is only a fallback if you can't supply the details (#73). */}
        {isFederal && !isPersonalFunds && (
          <p className="mt-2 text-xs text-gray-500">
            You can record this bank or third-party loan here — it files its Schedule C ledger.
            Once it&apos;s saved, add the loan-agreement details and any guarantors on the loan
            below and they file with your report; you only need the FEC portal if a detail is
            missing.
          </p>
        )}

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        <button
          type="button"
          disabled={!canSubmit || createLoan.isPending}
          onClick={handleCreate}
          className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {createLoan.isPending ? 'Saving…' : 'Record loan'}
        </button>
      </div>
    </div>
  );
}

function LoanRow({
  loan,
  isFederal,
  institutionOptions,
}: {
  loan: LoanDto;
  isFederal: boolean;
  institutionOptions: FinanceContactDto[];
}) {
  const updateBalance = useUpdateLoanBalance();
  const voidLoan = useVoidLoan();
  const unvoidLoan = useUnvoidLoan();
  const [editing, setEditing] = useState(false);
  const [balance, setBalance] = useState(String(loan.outstandingBalance));
  const [paid, setPaid] = useState(String(loan.paidToDate));
  const [error, setError] = useState<string | null>(null);

  // a removed (voided) loan drops from the filing; the row stays visible so the
  // treasurer can restore it (and keeps the forensic record). Muted while removed.
  const removed = loan.voidedAtUtc != null;

  // Divergence nudge mirror: surface inline when the loan's own numbers
  // don't reconcile (balance ≠ amount − paid) so the treasurer fixes it before filing.
  const diverges = loan.outstandingBalance !== loan.amount - loan.paidToDate;

  const handleSave = async () => {
    setError(null);
    try {
      await updateBalance.mutateAsync({
        id: loan.id,
        data: { outstandingBalance: Number(balance), paidToDate: Number(paid) },
      });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update balance');
    }
  };

  const handleRemove = async () => {
    setError(null);
    try {
      await voidLoan.mutateAsync(loan.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove loan');
    }
  };

  const handleRestore = async () => {
    setError(null);
    try {
      await unvoidLoan.mutateAsync(loan.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to restore loan');
    }
  };

  return (
    <div className={`rounded-md border p-3 text-sm ${removed ? 'bg-gray-50 opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium">{loan.contactName ?? 'Lender'}</span>
          <span className="ml-2 text-gray-500">{money(loan.amount)} on {loan.date}</span>
          {loan.isPersonalFunds && (
            <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
              Personal funds
            </span>
          )}
          {removed && (
            <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-700">
              Removed
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {removed ? (
            <button
              type="button"
              disabled={unvoidLoan.isPending}
              onClick={handleRestore}
              className="text-blue-600 hover:underline disabled:opacity-50"
            >
              {unvoidLoan.isPending ? 'Restoring…' : 'Restore'}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing((v) => !v)}
                className="text-blue-600 hover:underline"
              >
                {editing ? 'Cancel' : 'Update balance'}
              </button>
              <button
                type="button"
                disabled={voidLoan.isPending}
                onClick={handleRemove}
                className="text-red-600 hover:underline disabled:opacity-50"
              >
                {voidLoan.isPending ? 'Removing…' : 'Remove loan'}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="mt-1 text-gray-600">
        Outstanding {money(loan.outstandingBalance)} · paid {money(loan.paidToDate)}
      </div>
      {removed && (
        <p className="mt-1 text-xs text-gray-500">
          This loan was entered in error and won&apos;t appear on your report. If you already
          filed a report that included it, removing it here means your next filing for that
          period will be an amendment. Restore it if you removed it by mistake.
        </p>
      )}
      {diverges && !editing && !removed && (
        <p className="mt-1 text-xs text-amber-700">
          These numbers don&apos;t add up (balance ≠ amount − paid). Update them so they line
          up before filing.
        </p>
      )}
      {editing && !removed && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="text-xs">
            <span className="mb-1 block font-medium">Outstanding balance</span>
            <input
              aria-label="Outstanding balance"
              type="number"
              min={0}
              step={0.01}
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              className="w-32 rounded-md border px-2 py-1"
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-medium">Paid to date</span>
            <input
              aria-label="Paid to date"
              type="number"
              min={0}
              step={0.01}
              value={paid}
              onChange={(e) => setPaid(e.target.value)}
              className="w-32 rounded-md border px-2 py-1"
            />
          </label>
          <button
            type="button"
            disabled={updateBalance.isPending}
            onClick={handleSave}
            className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
          >
            {updateBalance.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {/* Personal-funds (candidate self-loan) loans have no guarantors by definition.
          A removed loan hides its editor — there is nothing to file. Guarantors emit
          federally via Schedule C-2 (and on state filings that carry them, e.g. the AL loan
          receipt, LA Schedule B), so the editor shows for any active bank / third-party loan.
          The row cap inside is read from the profile (`loanGuarantorCap`) — only AL declares one
          (3); federal and every other jurisdiction are uncapped (#73, #480).
          VA/NC file no guarantors at all, but the profile carries no `filesLoanGuarantors`
          signal and a null `loanGuarantorCap` can't tell VA/NC (files none) apart from LA/federal
          (uncapped, files any number) — so we can neither hide the editor nor add correct
          "won't file, add it in the portal" guidance without wrongly hiding/mislabeling LA. We
          keep it visible (VA's `VirginiaLoanCoBorrowerNotFiled` preflight warning already nudges
          "won't file"); a targeted hide/guidance is a follow-up gated on a new profile field. */}
      {!loan.isPersonalFunds && !removed && <GuarantorSection loan={loan} />}

      {/* The Schedule C-1 loan-agreement editor is FEDERAL-only: it captures the signed
          bank-loan agreement (secured/collateral, lending-rep name + title, signature dates)
          that the FEC Form 3 needs, which has no state-filing analog. Shown for any active
          federal loan — INCLUDING a candidate's own, because a candidate who re-lends a bank,
          HELOC, brokerage or credit-card draw owes the same C-1 (11 CFR 104.3(d)(4)) and this
          editor is where they name the institution behind it. A candidate who lent their own
          cash names none, and the section stays a one-line no-op for them. */}
      {isFederal && !removed && (
        <LoanAgreementSection loan={loan} institutionOptions={institutionOptions} />
      )}
    </div>
  );
}

// #73 — manage the loan's guarantors. A loan backed by a bank or third party can
// name guarantors; each appears on the loan's filing with their name, address, and the amount
// they guarantee. The per-loan count cap is jurisdiction-declared: only Alabama caps it
// (Ala. Code §17-5-2(a)(3)(a): max 3); the FEC (Schedule C-2) and every other jurisdiction
// impose no cap, so those loans can name any number.
function GuarantorSection({ loan }: { loan: LoanDto }) {
  const setGuarantors = useSetLoanGuarantors();
  // Name the jurisdiction in the cap explanation when we know it (mirrors the
  // loan-card intro copy) so treasurers see "Alabama law caps…" not a bare
  // "State law…". Never branch on isAlabama — read the display name off the profile.
  const profile = readyProfile(useComplianceProfile());
  const capExplanationId = `guarantor-cap-${loan.id}`;
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<LoanGuarantorInput[]>([]);
  const [error, setError] = useState<string | null>(null);

  // #480 — the row cap is the jurisdiction's DECLARED `loanGuarantorCap`, never an isFederal
  // binary. null = uncapped (federal, VA, NC, LA): no row limit and no "caps guarantors at 3"
  // copy. Only Alabama declares a real cap (3), so that copy only ever shows there.
  const guarantorCap = profile?.loanGuarantorCap ?? null;

  const startEditing = () => {
    setError(null);
    setRows(
      loan.guarantors.map((g) => ({
        fullName: g.fullName,
        fullAddress: g.fullAddress,
        amount: g.amount,
      })),
    );
    setEditing(true);
  };

  const updateRow = (index: number, patch: Partial<LoanGuarantorInput>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const addRow = () =>
    setRows((prev) =>
      guarantorCap !== null && prev.length >= guarantorCap
        ? prev
        : [...prev, { fullName: '', fullAddress: '', amount: 0 }],
    );

  const removeRow = (index: number) =>
    setRows((prev) => prev.filter((_, i) => i !== index));

  const canSave = rows.every(
    (r) => r.fullName.trim() !== '' && r.fullAddress.trim() !== '' && r.amount > 0,
  );

  // Only a state cap can be "hit"; federal (guarantorCap === null) is never at a cap.
  const atCap = guarantorCap !== null && rows.length >= guarantorCap;
  const capJurisdiction = profile?.stateDisplayName ?? 'State';
  const capExplanation = `${capJurisdiction} law caps guarantors at ${guarantorCap} per loan. Remove one to add a different guarantor.`;

  const handleSave = async () => {
    setError(null);
    try {
      await setGuarantors.mutateAsync({
        id: loan.id,
        data: {
          guarantors: rows.map((r) => ({
            fullName: r.fullName.trim(),
            fullAddress: r.fullAddress.trim(),
            amount: r.amount,
          })),
        },
      });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save guarantors');
    }
  };

  return (
    <div className="mt-2 border-t pt-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">
          Guarantors{loan.guarantors.length > 0 ? ` (${loan.guarantors.length})` : ''}
        </span>
        <button
          type="button"
          onClick={() => (editing ? setEditing(false) : startEditing())}
          className="text-xs text-blue-600 hover:underline"
        >
          {editing ? 'Cancel' : loan.guarantors.length > 0 ? 'Edit guarantors' : 'Add guarantors'}
        </button>
      </div>

      {!editing && loan.guarantors.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
          {loan.guarantors.map((g) => (
            <li key={g.id}>
              {g.fullName} — {money(g.amount)}
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="mt-2 space-y-3">
          <p className="text-xs text-gray-500">
            {guarantorCap !== null
              ? `A loan can name up to ${guarantorCap} guarantors. `
              : 'Name each person who guarantees this loan. '}
            Enter each one&apos;s full name, full address, and the amount they guarantee.
          </p>
          {rows.map((row, i) => (
            <div key={i} className="flex flex-wrap items-end gap-2">
              <label className="text-xs">
                <span className="mb-1 block font-medium">Full name</span>
                <input
                  aria-label={`Guarantor ${i + 1} full name`}
                  type="text"
                  maxLength={200}
                  value={row.fullName}
                  onChange={(e) => updateRow(i, { fullName: e.target.value })}
                  className="w-40 rounded-md border px-2 py-1"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-medium">Full address</span>
                <input
                  aria-label={`Guarantor ${i + 1} full address`}
                  type="text"
                  maxLength={500}
                  value={row.fullAddress}
                  onChange={(e) => updateRow(i, { fullAddress: e.target.value })}
                  className="w-56 rounded-md border px-2 py-1"
                />
              </label>
              <label className="text-xs">
                <span className="mb-1 block font-medium">Amount</span>
                <input
                  aria-label={`Guarantor ${i + 1} amount`}
                  type="number"
                  min={0}
                  step={0.01}
                  value={row.amount === 0 ? '' : String(row.amount)}
                  onChange={(e) =>
                    updateRow(i, { amount: e.target.value === '' ? 0 : Number(e.target.value) })
                  }
                  className="w-28 rounded-md border px-2 py-1"
                />
              </label>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="pb-1 text-xs text-red-600 hover:underline"
              >
                Remove
              </button>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={addRow}
              disabled={atCap}
              aria-disabled={atCap}
              aria-describedby={atCap ? capExplanationId : undefined}
              className="rounded-md border px-3 py-1 text-xs font-medium disabled:opacity-50"
            >
              Add guarantor
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || setGuarantors.isPending}
              className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
            >
              {setGuarantors.isPending ? 'Saving…' : 'Save guarantors'}
            </button>
          </div>

          {/* At the cap, explain WHY "Add guarantor" is disabled — a disabled button
              can't reliably carry a hover title and screen readers may skip it, so we
              show visible text and point the button at it via aria-describedby. Wraps
              cleanly at large/bold font scales (no truncation). */}
          {atCap && (
            <p id={capExplanationId} className="text-xs text-gray-500">
              {capExplanation}
            </p>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}

// #73 — the FEC Schedule C-1 loan-agreement editor. A federal bank / third-party loan needs
// its signed loan-agreement details on file to report the agreement (Schedule C-1) with the
// Form 3, instead of finishing it in the FEC portal: whether the loan is secured (and by
// what), the lending institution's representative (name + title), and the two signature dates
// (treasurer side, lender side). Collapsible + Tailwind, mirroring GuarantorSection. Treasurer
// summary reuses the committee treasurer name already on file (no field here). Large, labeled
// inputs and wrapping copy for large/bold-text accessibility.
function LoanAgreementSection({
  loan,
  institutionOptions,
}: {
  loan: LoanDto;
  institutionOptions: FinanceContactDto[];
}) {
  const setAgreement = useSetLoanAgreement();
  const [editing, setEditing] = useState(false);
  const [lendingInstitutionContactId, setLendingInstitutionContactId] = useState('');
  const [isSecured, setIsSecured] = useState(false);
  const [collateralDescription, setCollateralDescription] = useState('');
  const [collateralValue, setCollateralValue] = useState('');
  const [lendingRepFirstName, setLendingRepFirstName] = useState('');
  const [lendingRepMiddleName, setLendingRepMiddleName] = useState('');
  const [lendingRepLastName, setLendingRepLastName] = useState('');
  const [lendingRepTitle, setLendingRepTitle] = useState('');
  const [treasurerSignedDate, setTreasurerSignedDate] = useState('');
  const [lenderSignedDate, setLenderSignedDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Seed the form from what's already on the loan so opening the editor doesn't blank
  // previously-entered agreement details.
  const startEditing = () => {
    setError(null);
    setLendingInstitutionContactId(loan.lendingInstitutionContactId ?? '');
    setIsSecured(loan.isSecured);
    setCollateralDescription(loan.collateralDescription ?? '');
    setCollateralValue(loan.collateralValue != null ? String(loan.collateralValue) : '');
    setLendingRepFirstName(loan.lendingRepFirstName ?? '');
    setLendingRepMiddleName(loan.lendingRepMiddleName ?? '');
    setLendingRepLastName(loan.lendingRepLastName ?? '');
    setLendingRepTitle(loan.lendingRepTitle ?? '');
    setTreasurerSignedDate(loan.treasurerSignedDate ?? '');
    setLenderSignedDate(loan.lenderSignedDate ?? '');
    setEditing(true);
  };

  // Whether any agreement detail is on file — drives the read-only "on file" hint and the
  // Add / Edit affordance label.
  const hasAgreement =
    !!loan.lendingInstitutionContactId ||
    loan.isSecured ||
    !!loan.lendingRepLastName ||
    !!loan.lendingRepFirstName ||
    !!loan.lendingRepTitle ||
    !!loan.treasurerSignedDate ||
    !!loan.lenderSignedDate;

  // A candidate who lent their own savings owes no agreement at all: no institution means no
  // representative to name and no signatures to date, so asking would imply paperwork they
  // don't owe. Every other loan has an institution by construction.
  const needsAgreementDetail = !loan.isPersonalFunds || lendingInstitutionContactId !== '';

  const trimOrNull = (v: string) => (v.trim() === '' ? null : v.trim());

  const handleSave = async () => {
    setError(null);
    try {
      // Same rule the collateral fields follow: detail belongs to the thing that requires it,
      // so dropping that thing drops the detail. With no institution there is no agreement to
      // report, and leaving stale signatures behind would keep the row claiming one is on file.
      const detail = needsAgreementDetail;
      const data: SetLoanAgreementRequest = {
        // Only a candidate loan names a separate institution; on a bank loan the lender
        // contact already is one, and the server rejects a second.
        lendingInstitutionContactId: loan.isPersonalFunds
          ? lendingInstitutionContactId || null
          : null,
        isSecured: detail && isSecured,
        // Only send collateral when the loan is secured; clearing "secured" drops it.
        collateralDescription: detail && isSecured ? trimOrNull(collateralDescription) : null,
        collateralValue:
          detail && isSecured && collateralValue.trim() !== '' ? Number(collateralValue) : null,
        lendingRepFirstName: detail ? trimOrNull(lendingRepFirstName) : null,
        lendingRepMiddleName: detail ? trimOrNull(lendingRepMiddleName) : null,
        lendingRepLastName: detail ? trimOrNull(lendingRepLastName) : null,
        lendingRepTitle: detail ? trimOrNull(lendingRepTitle) : null,
        treasurerSignedDate: detail ? trimOrNull(treasurerSignedDate) : null,
        lenderSignedDate: detail ? trimOrNull(lenderSignedDate) : null,
      };
      await setAgreement.mutateAsync({ id: loan.id, data });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save loan agreement');
    }
  };

  return (
    <div className="mt-2 border-t pt-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Loan agreement{hasAgreement ? ' (on file)' : ''}
        </span>
        <button
          type="button"
          onClick={() => (editing ? setEditing(false) : startEditing())}
          className="text-sm text-blue-600 hover:underline"
        >
          {editing ? 'Cancel' : hasAgreement ? 'Edit loan agreement' : 'Add loan agreement'}
        </button>
      </div>

      {!editing && (
        <p className="mt-1 text-xs text-gray-500">
          {loan.isPersonalFunds
            ? `Did the candidate borrow this money — a bank loan, home-equity line, brokerage
               line or credit card — and lend it to the campaign? If so, name the lender here:
               the FEC needs that loan's terms reported with your Form 3. If the candidate lent
               their own savings, leave this alone; there is nothing more to file.`
            : `A bank loan needs its signed loan-agreement details on file so the agreement
               files with your report. Add the lending representative's name and title, whether
               the loan is secured, and the dates you and the lender signed. If you skip it,
               you can finish the agreement in the FEC portal after you upload.`}
        </p>
      )}

      {editing && (
        <div className="mt-2 space-y-3">
          {/* A candidate is the campaign's lender, so the loan row names them, not the bank
              they drew on. This is the only place that fact can be recorded. */}
          {loan.isPersonalFunds && (
            <label className="block text-sm">
              <span className="mb-1 block font-medium">
                Lending institution the candidate borrowed from (optional)
              </span>
              <div className="flex max-w-sm items-center gap-2">
                <ContactCombobox
                  ariaLabel="Lending institution"
                  value={lendingInstitutionContactId}
                  onChange={setLendingInstitutionContactId}
                  contacts={institutionOptions}
                  placeholder="None — the candidate lent their own money"
                  emptyText="No organization contacts yet — add the bank as a contact first."
                />
                {/* The shared picker cannot un-pick, and a treasurer who named the wrong
                    lender — or who realises the money was the candidate's own after all —
                    needs a way back to "no institution", which is a legitimate answer here. */}
                {lendingInstitutionContactId !== '' && (
                  <button
                    type="button"
                    aria-label="Clear lending institution"
                    onClick={() => setLendingInstitutionContactId('')}
                    className="shrink-0 text-sm text-blue-600 hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </label>
          )}

          {needsAgreementDetail && (
          <>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              aria-label="This loan is secured"
              checked={isSecured}
              onChange={(e) => setIsSecured(e.target.checked)}
              className="h-5 w-5 rounded border"
            />
            <span>This loan is secured (backed by collateral)</span>
          </label>

          {isSecured && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="mb-1 block font-medium">Collateral description</span>
                <input
                  aria-label="Collateral description"
                  type="text"
                  maxLength={100}
                  value={collateralDescription}
                  onChange={(e) => setCollateralDescription(e.target.value)}
                  className="w-64 rounded-md border px-3 py-2"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block font-medium">Collateral value</span>
                <input
                  aria-label="Collateral value"
                  type="number"
                  min={0}
                  step={0.01}
                  value={collateralValue}
                  onChange={(e) => setCollateralValue(e.target.value)}
                  className="w-40 rounded-md border px-3 py-2"
                />
              </label>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm">
              <span className="mb-1 block font-medium">Lending representative first name</span>
              <input
                aria-label="Lending representative first name"
                type="text"
                maxLength={20}
                value={lendingRepFirstName}
                onChange={(e) => setLendingRepFirstName(e.target.value)}
                className="w-44 rounded-md border px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">
                Lending representative middle name (optional)
              </span>
              <input
                aria-label="Lending representative middle name"
                type="text"
                maxLength={20}
                value={lendingRepMiddleName}
                onChange={(e) => setLendingRepMiddleName(e.target.value)}
                className="w-44 rounded-md border px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Lending representative last name</span>
              <input
                aria-label="Lending representative last name"
                type="text"
                maxLength={30}
                value={lendingRepLastName}
                onChange={(e) => setLendingRepLastName(e.target.value)}
                className="w-44 rounded-md border px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Lending representative title</span>
              <input
                aria-label="Lending representative title"
                type="text"
                maxLength={20}
                placeholder="e.g. Vice President"
                value={lendingRepTitle}
                onChange={(e) => setLendingRepTitle(e.target.value)}
                className="w-56 rounded-md border px-3 py-2"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm">
              <span className="mb-1 block font-medium">Treasurer signed date</span>
              <input
                aria-label="Treasurer signed date"
                type="date"
                value={treasurerSignedDate}
                onChange={(e) => setTreasurerSignedDate(e.target.value)}
                className="rounded-md border px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Lender signed date</span>
              <input
                aria-label="Lender signed date"
                type="date"
                value={lenderSignedDate}
                onChange={(e) => setLenderSignedDate(e.target.value)}
                className="rounded-md border px-3 py-2"
              />
            </label>
          </div>
          </>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={setAgreement.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {setAgreement.isPending ? 'Saving…' : 'Save agreement'}
          </button>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
